import { Component, ReactNode, ErrorInfo } from "react";
import i18n from "@/i18n";
import { t } from "@/i18n/utils";
import { isChunkLoadError } from "@/utils/chunkReload";

// 取翻译，缺 key / i18n 未就绪时回退到硬编码英文，绝不显示原始 key。
function tx(key: string, fallback: string): string {
  try {
    return i18n.exists(key) ? String(t(key)) : fallback;
  } catch {
    return fallback;
  }
}

/**
 * 极简、零依赖、自包含的"加载失败"卡片。
 *
 * 关键约束：它恰好在"某个 chunk 加载失败"时渲染，自身绝不能再依赖任何
 * 懒加载 / vendor chunk（否则最坏情况连依赖都拉不到，卡片自己也崩 → 还是白屏）。
 * 因此只用原生元素 + 内联样式；文案复用已存在的 errors.* key（en/pt/es/ar/bn
 * 均已就绪）。居中单列布局天然兼容 RTL（阿拉伯语）。
 */
export function ChunkErrorCard() {
  const title = tx("errors.loadFailed", "Load failed");
  const retry = tx("errors.retry", "Retry");
  return (
    <div
      style={{
        minHeight: "60vh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 16,
        padding: 24,
        textAlign: "center",
        boxSizing: "border-box",
      }}
    >
      <div style={{ fontSize: 15, lineHeight: 1.5, color: "#cfd2da" }}>
        {title}
      </div>
      <button
        type="button"
        onClick={() => window.location.reload()}
        style={{
          padding: "9px 32px",
          fontSize: 15,
          fontWeight: 600,
          color: "#1a1a1a",
          background: "#f5b400",
          border: "none",
          borderRadius: 8,
          cursor: "pointer",
        }}
      >
        {retry}
      </button>
    </div>
  );
}

interface BoundaryProps {
  children: ReactNode;
}
interface BoundaryState {
  error: Error | null;
}

/**
 * 路由级错误边界：
 *  - chunk 加载错误 → 渲染 ChunkErrorCard。
 *    （自动刷新已在 withChunkReload 的 import .catch 内尝试过；走到这里说明刷新
 *     预算已用尽，展示卡片让用户手动重试，绝不无限刷。）
 *  - 其它运行时错误 → 原样向上抛，交 React Router 默认错误处理，
 *    绝不把页面真 bug 当成 chunk 错误吞掉或刷新。
 */
export class ChunkLoadBoundary extends Component<BoundaryProps, BoundaryState> {
  state: BoundaryState = { error: null };

  static getDerivedStateFromError(error: Error): BoundaryState {
    return { error };
  }

  render() {
    const { error } = this.state;
    if (error) {
      if (isChunkLoadError(error)) {
        return <ChunkErrorCard />;
      }
      // 非 chunk 错误：交给上层边界（React Router 默认错误页），保持现有行为。
      throw error;
    }
    return this.props.children;
  }
}

interface SilentProps {
  children: ReactNode;
}
interface SilentState {
  hasError: boolean;
}

/**
 * 静默错误边界：用于 App.tsx 里的非关键后台懒加载弹窗。
 *
 * 任何错误（含 chunk 加载失败）都只渲染 null —— 绝不刷新、绝不白屏。后台弹窗
 * 加载失败对用户无感即可，避免一个弹窗 chunk 失败把整个 app 拖成错误页
 * （修复现状：这些 lazy 弹窗此前无错误边界，失败会冒泡到 React Router 白屏）。
 */
export class SilentErrorBoundary extends Component<SilentProps, SilentState> {
  state: SilentState = { hasError: false };

  static getDerivedStateFromError(): SilentState {
    return { hasError: true };
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    if (isChunkLoadError(error)) {
      console.warn("[lazy popup] chunk load failed, skipped:", error?.message);
    } else {
      console.error("[lazy popup] render error, skipped:", error, info);
    }
  }

  render() {
    return this.state.hasError ? null : this.props.children;
  }
}
